import { isProd } from '@shared/env';
import { AndroidAbilityCheck } from '@shared/utils/platform/platformDetection';
import { LSK } from '@shared/utils/storage/config';
import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

/**
 * 用户渠道类型（持久化数字勿改含义）
 * 历史：1 ad, 2 h5, 3 share, 4 other, 5 unknown
 * 现用名：META=原 ad(1)、SEO=原 h5(2)；AD_ORIGIN_USER / H5_ORIGIN_USER 为同值别名
 * 新增：仅 GOOGLE、ASO 接在 5 后面
 */
export enum UserChannelType {
	META_ORIGIN_USER = 1,
	SEO_ORIGIN_USER = 2,
	SHARE_ORIGIN_USER = 3,
	OTHER_USER = 4,
	UNKNOWN_USER = 5,
	GOOGLE_ORIGIN_USER = 6,
	ASO_ORIGIN_USER = 7,
}

export const UserChannelVersionMap: Record<UserChannelType, string> = {
	[UserChannelType.META_ORIGIN_USER]: 'meta',
	[UserChannelType.SEO_ORIGIN_USER]: 'seo',
	[UserChannelType.SHARE_ORIGIN_USER]: 'share',
	[UserChannelType.OTHER_USER]: 'other',
	[UserChannelType.UNKNOWN_USER]: 'unknown',
	[UserChannelType.GOOGLE_ORIGIN_USER]: 'google',
	[UserChannelType.ASO_ORIGIN_USER]: 'aso',
};

interface UserTypeState {
	// 用户包类型
	userType: UserChannelType;
	// 通知权限是否开启
	areNotificationsEnabled: boolean;
	setUserType: (userType: UserChannelType) => void;
	checkAreNotificationsEnabled: () => boolean;
	initUserTypeWithReferrer: (googlePlayReferrer: string) => void;
}

export const useUserTypeStore = create(
	subscribeWithSelector(
		persist(
			immer<UserTypeState>((set, get) => ({
				userType: UserChannelType.UNKNOWN_USER,
				areNotificationsEnabled: false,
				checkAreNotificationsEnabled: () => {
					if (AndroidAbilityCheck.isGetNotificationsEnabledAvailable) {
						const areNotificationsEnabled = !!window.AndroidInterface?.areNotificationsEnabled?.();
						set({ areNotificationsEnabled: areNotificationsEnabled });
						return areNotificationsEnabled;
					}
					return false;
				},
				setUserType: (userType: UserChannelType) => {
					set({ userType });
				},
				initUserTypeWithReferrer: (googlePlayReferrer: string) => {
					// 测试环境优先走测试环境设置的用户类型
					if (!isProd) {
						const userType = localStorage.getItem(LSK.USER_TYPE_FOR_TEST);
						if (userType) {
							set({ userType: Number(userType) });
							return;
						}
					}
					if (googlePlayReferrer.includes('utm_source=share')) {
						set({ userType: UserChannelType.SHARE_ORIGIN_USER });
					} else if (
						googlePlayReferrer.includes('utm_source=h5') ||
						googlePlayReferrer.includes('utm_source=h5_register') ||
						googlePlayReferrer.includes('utm_source=h5_generate') ||
						googlePlayReferrer.includes('utm_source=h5_seopopup') ||
						googlePlayReferrer.includes('utm_source=h5_navigation') ||
						googlePlayReferrer.includes('utm_source=h5_hero')
					) {
						set({ userType: UserChannelType.SEO_ORIGIN_USER });
					} else if (
						googlePlayReferrer.includes('utm_source=apps.instagram.com') ||
						googlePlayReferrer.includes('utm_source=apps.facebook.com')
					) {
						set({ userType: UserChannelType.META_ORIGIN_USER });
					} else if (
						googlePlayReferrer.includes('gclid=') ||
						googlePlayReferrer.includes('gbraid=')
					) {
						set({ userType: UserChannelType.GOOGLE_ORIGIN_USER });
					} else {
						if (get().userType === UserChannelType.UNKNOWN_USER) {
							set({ userType: UserChannelType.OTHER_USER });
						}
					}
				},
			})),
			{
				name: 'userType',
			},
		),
	),
);
